/**
 * Shared definition + validation for the public website forms.
 *
 * The site has no form backend. A completed form composes an email: client-side
 * validation runs here, then the browser opens the visitor's mail app with the
 * details prepared and addressed to the KleipData enquiries mailboxes. The
 * visitor sends it. This module is pure (no React / Next / Node) so it is
 * trivially testable and safe to import anywhere.
 */

/** Where a prepared enquiry email is addressed. */
export const ENQUIRY_RECIPIENTS = ["support@kleipdata.co.uk", "ask@computerko.com"] as const;

export type FormKind =
  | "get-access"
  | "request-demo"
  | "contact"
  | "support"
  | "pricing-comparison";

export const FORM_KINDS: FormKind[] = [
  "get-access",
  "request-demo",
  "contact",
  "support",
  "pricing-comparison",
];

/** Product areas a visitor can ask about. Mirrors the KleipData product surface. */
export const PRODUCT_AREAS = [
  "Person Intelligence",
  "Company Intelligence",
  "Data Services",
  "Bulk & Enrichment",
  "API Access",
  "Multiple services",
] as const;

export const DEMO_AREAS = [
  "Person Intelligence",
  "Company Intelligence",
  "Data Services",
  "Bulk & Enrichment",
  "API",
  "Full platform",
] as const;

export const CONTACT_SUBJECTS = [
  "Sales & pricing",
  "API & integration",
  "Procurement",
  "Partnership",
  "General enquiry",
] as const;

export const SUPPORT_SUBJECTS = [
  "Platform support",
  "API support",
  "Integration support",
  "Data / search support",
  "Account support",
  "Billing support",
] as const;

type FieldType = "text" | "email" | "textarea" | "select";

interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  /** allowed values for a select */
  options?: readonly string[];
  maxLength: number;
  autoComplete?: string;
}

const F = (
  name: string,
  label: string,
  type: FieldType,
  required: boolean,
  extra: Partial<FieldDef> = {},
): FieldDef => ({ name, label, type, required, maxLength: 500, ...extra });

const FULL_NAME = F("fullName", "Full name", "text", true, { maxLength: 120, autoComplete: "name" });
const WORK_EMAIL = F("workEmail", "Work email", "email", true, { maxLength: 254, autoComplete: "email" });
const ORGANISATION = F("organisation", "Organisation", "text", true, { maxLength: 160, autoComplete: "organization" });
const JOB_TITLE = F("jobTitle", "Job title", "text", false, { maxLength: 120, autoComplete: "organization-title" });
const MESSAGE = F("message", "Message", "textarea", false, { maxLength: 4000 });

export const FORM_FIELDS: Record<FormKind, FieldDef[]> = {
  "get-access": [
    FULL_NAME,
    WORK_EMAIL,
    ORGANISATION,
    JOB_TITLE,
    F("accessTo", "What would you like access to?", "select", true, { options: PRODUCT_AREAS }),
    F("volume", "Expected usage / search volume", "text", false, { maxLength: 200 }),
    MESSAGE,
  ],
  "request-demo": [
    FULL_NAME,
    WORK_EMAIL,
    ORGANISATION,
    JOB_TITLE,
    F("interestedIn", "What would you like to see?", "select", true, { options: DEMO_AREAS }),
    F("preferredTime", "Preferred date/time", "text", false, { maxLength: 200 }),
    F("message", "Anything you'd like us to cover?", "textarea", false, { maxLength: 4000 }),
  ],
  contact: [
    FULL_NAME,
    WORK_EMAIL,
    F("organisation", "Organisation", "text", false, { maxLength: 160, autoComplete: "organization" }),
    F("subject", "Subject", "select", true, { options: CONTACT_SUBJECTS }),
    F("message", "Message", "textarea", true, { maxLength: 4000 }),
  ],
  support: [
    FULL_NAME,
    F("workEmail", "Email", "email", true, { maxLength: 254, autoComplete: "email" }),
    F("organisation", "Organisation", "text", false, { maxLength: 160, autoComplete: "organization" }),
    F("subject", "Subject", "select", true, { options: SUPPORT_SUBJECTS }),
    F("message", "Message", "textarea", true, { maxLength: 4000 }),
    F("reference", "Request ID / JOB-II / PER-II / CO-II", "text", false, { maxLength: 80 }),
  ],
  "pricing-comparison": [
    FULL_NAME,
    WORK_EMAIL,
    ORGANISATION,
    F("users", "Number of users", "text", false, { maxLength: 60 }),
    F("currentSystem", "Current system or providers", "text", false, { maxLength: 200 }),
    F("searchVolume", "Approximate monthly search volume", "text", false, { maxLength: 120 }),
    F("integration", "Integration requirements", "text", false, { maxLength: 200 }),
    F("message", "Anything else we should factor in?", "textarea", false, { maxLength: 4000 }),
  ],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ValidationResult {
  ok: boolean;
  /** field name → message, only for fields that failed */
  fieldErrors: Record<string, string>;
  /** trimmed, whitelisted values in field order */
  clean: Record<string, string>;
}

const FORM_TITLE: Record<FormKind, string> = {
  "get-access": "Access request",
  "request-demo": "Demo request",
  contact: "Enquiry",
  support: "Support request",
  "pricing-comparison": "Tailored cost comparison request",
};

/**
 * Build the `mailto:` a validated form submits to. Recipients are the KleipData
 * enquiries mailboxes; the body carries every provided field, labelled.
 */
export function buildEnquiryMailto(kind: FormKind, values: Record<string, string>): string {
  const fields = FORM_FIELDS[kind] ?? [];
  const title = FORM_TITLE[kind] ?? "Enquiry";

  const lines: string[] = [`KleipData website — ${title}`, ""];
  for (const field of fields) {
    const value = (values[field.name] ?? "").trim();
    lines.push(`${field.label}:`, value || "Not provided", "");
  }

  const subjectValue = (values.subject ?? values.accessTo ?? values.interestedIn ?? "").trim();
  const subject = `KleipData ${title}${subjectValue ? ` — ${subjectValue}` : ""}`;

  return (
    `mailto:${ENQUIRY_RECIPIENTS.join(",")}` +
    `?subject=${encodeURIComponent(subject)}` +
    `&body=${encodeURIComponent(lines.join("\r\n"))}`
  );
}

/** Authoritative validation for one form submission. */
export function validateForm(kind: FormKind, raw: unknown): ValidationResult {
  const fieldErrors: Record<string, string> = {};
  const clean: Record<string, string> = {};

  const fields = FORM_FIELDS[kind];
  if (!fields) {
    return { ok: false, fieldErrors: { _form: "Unknown form." }, clean };
  }

  const data = (raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}) as Record<string, unknown>;

  for (const field of fields) {
    const value = typeof data[field.name] === "string" ? (data[field.name] as string).trim() : "";

    if (!value) {
      if (field.required) fieldErrors[field.name] = requiredMessage(field);
      continue;
    }

    if (value.length > field.maxLength) {
      fieldErrors[field.name] = `${field.label} is too long.`;
      continue;
    }

    if (field.type === "email" && !EMAIL_RE.test(value)) {
      fieldErrors[field.name] = "Enter a valid work email.";
      continue;
    }

    if (field.type === "select" && field.options && !field.options.includes(value)) {
      fieldErrors[field.name] = `Select a valid option for “${field.label}”.`;
      continue;
    }

    clean[field.name] = value;
  }

  return { ok: Object.keys(fieldErrors).length === 0, fieldErrors, clean };
}

const REQUIRED_MESSAGE: Record<string, string> = {
  fullName: "Enter your name.",
  workEmail: "Enter a valid work email.",
  organisation: "Enter your organisation.",
  accessTo: "Select what you'd like access to.",
  interestedIn: "Select what you'd like to see.",
  subject: "Select a subject.",
  message: "Enter a message.",
};

function requiredMessage(field: FieldDef): string {
  if (REQUIRED_MESSAGE[field.name]) return REQUIRED_MESSAGE[field.name]!;
  if (field.type === "select") return `Select ${field.label.toLowerCase().replace(/\?$/, "")}.`;
  if (field.type === "email") return "Enter a valid work email.";
  return `Enter ${field.label.toLowerCase()}.`;
}
